import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import { allArticles, allCourses } from '@/mocks/data';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();

  const article = allArticles.find((a) => a.slug === slug);
  const course = allCourses.find((c) => c.slug === slug);

  if (!article && !course) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main>
          <section className="pt-24 pb-16 md:pt-32 md:pb-20 bg-background-50 min-h-screen flex items-center justify-center">
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-file-unknow-line text-3xl"></i>
              </div>
              <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-2">内容未找到</h1>
              <p className="text-foreground-600 mb-6">抱歉，您要查看的内容不存在或已被移除。</p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors"
              >
                返回首页
              </Link>
            </div>
          </section>
        </main>
        <Footer />
      </>
    );
  }

  if (article) {
    const seoTitle = `${article.title}——学至成学习文章`;
    const articleSchema = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description: article.excerpt,
      author: { '@type': 'Person', name: article.author },
      datePublished: article.date,
      image: article.image,
      url: `https://hanafsih.cn/article/${article.slug}`,
      inLanguage: 'zh-CN',
    };

    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://hanafsih.cn/' },
        { '@type': 'ListItem', position: 2, name: '学习文章', item: 'https://hanafsih.cn/article/career-change-guide-2026' },
        { '@type': 'ListItem', position: 3, name: article.title },
      ],
    };

    return (
      <>
        <ScrollToTop />
        <SEOHead
          title={seoTitle}
          description={article.excerpt}
          keywords={`成人在线学习,${article.title},职业技能`}
          canonicalUrl={`https://hanafsih.cn/article/${article.slug}`}
          ogType="article"
          schemaData={{ '@context': 'https://schema.org', '@graph': [articleSchema, breadcrumbSchema] }}
        />
        <Navbar />

        <main>
          <article className="pt-24 pb-16 md:pt-32 md:pb-20 bg-background-50">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <Breadcrumb
                items={[
                  { label: '首页', path: '/' },
                  { label: '学习文章', path: '/article/career-change-guide-2026' },
                  { label: article.title },
                ]}
              />

              <header className="mb-8">
                <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-4 leading-tight">
                  {article.title}
                </h1>

                <div className="flex items-center gap-5 text-sm text-foreground-500 flex-wrap">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-user-line"></i> {article.author}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-calendar-line"></i> {article.date}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-time-line"></i> 阅读约{article.readTime}
                  </span>
                </div>
              </header>

              <div className="mb-10 rounded-2xl overflow-hidden">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-auto object-cover max-h-[400px]"
                />
              </div>

              <div
                className="prose-custom text-foreground-800 leading-relaxed text-base"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="mt-12 pt-8 border-t border-background-200">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div>
                    <p className="text-foreground-500 text-sm">作者</p>
                    <p className="font-medium text-foreground-900">{article.author}</p>
                  </div>
                  <Link
                    to="/category/it-programming"
                    className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
                  >
                    浏览相关课程 <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </div>
            </div>
          </article>
        </main>

        <Footer />
      </>
    );
  }

  if (course) {
    const seoTitle = `${course.title}——成人在线课程`;
    const productSchema = {
      '@context': 'https://schema.org',
      '@type': 'Course',
      name: course.title,
      description: course.description,
      provider: { '@type': 'Organization', name: '学至成' },
      url: `https://hanafsih.cn/article/${course.slug}`,
      inLanguage: 'zh-CN',
    };

    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://hanafsih.cn/' },
        { '@type': 'ListItem', position: 2, name: '课程分类', item: `https://hanafsih.cn/category/${course.category}` },
        { '@type': 'ListItem', position: 3, name: course.title },
      ],
    };

    const breadcrumbItems = [
      { label: '首页', path: '/' },
      { label: '课程分类', path: `/category/${course.category}` },
      { label: course.title },
    ];

    return (
      <>
        <ScrollToTop />
        <SEOHead
          title={seoTitle}
          description={course.description}
          keywords={`成人在线课程,${course.title},${course.category}`}
          canonicalUrl={`https://hanafsih.cn/article/${course.slug}`}
          schemaData={{ '@context': 'https://schema.org', '@graph': [productSchema, breadcrumbSchema] }}
        />
        <Navbar />

        <main>
          <section className="pt-24 pb-16 md:pt-32 md:pb-20 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <Breadcrumb items={breadcrumbItems} />

              <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 mt-4">
                <div className="lg:col-span-3">
                  <div className="rounded-2xl overflow-hidden mb-8">
                    <img
                      src={course.image}
                      alt={course.title}
                      className="w-full h-auto object-cover max-h-[420px]"
                    />
                  </div>

                  <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
                    {course.title}
                  </h1>

                  <div className="flex items-center gap-4 text-sm text-foreground-500 mb-6 flex-wrap">
                    <span className="flex items-center gap-1.5">
                      <i className="ri-user-line"></i> 讲师：{course.instructor}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <i className="ri-time-line"></i> 学习周期：{course.duration}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <i className="ri-bar-chart-box-line"></i> 难度：{course.level}
                    </span>
                    <span className="flex items-center gap-1.5 text-primary-600">
                      <i className="ri-star-fill"></i> {course.rating}分
                    </span>
                    <span>{course.students.toLocaleString()}人已学习</span>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-6">
                    {course.highlights.map((h) => (
                      <span key={h} className="px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium">
                        {h}
                      </span>
                    ))}
                  </div>

                  <div className="prose-custom text-foreground-800 leading-relaxed text-base">
                    <h3 className="font-heading text-xl font-semibold text-foreground-900 mb-3">课程简介</h3>
                    <p>{course.description}</p>

                    <h3 className="font-heading text-xl font-semibold text-foreground-900 mb-3 mt-8">适合人群</h3>
                    <ul>
                      <li>希望系统提升{activeCategoryName(course.category)}能力的职场人士</li>
                      <li>准备转行或转型到相关领域的从业者</li>
                      <li>希望获得行业认证和资格的专业人士</li>
                      <li>对{activeCategoryName(course.category)}感兴趣的零基础学员</li>
                    </ul>

                    <h3 className="font-heading text-xl font-semibold text-foreground-900 mb-3 mt-8">学习收获</h3>
                    <ul>
                      <li>系统掌握{activeCategoryName(course.category)}的核心知识和技能</li>
                      <li>通过实战项目积累实战经验</li>
                      <li>获得平台颁发的结业证书</li>
                      <li>加入学习社群，拓展行业人脉</li>
                    </ul>
                  </div>
                </div>

                <div className="lg:col-span-2">
                  <div className="sticky top-24 bg-background-100 rounded-2xl p-6 border border-background-200/60">
                    <div className="flex items-baseline gap-2 mb-5">
                      <span className="text-3xl font-bold text-primary-600">¥{course.price}</span>
                      <span className="text-lg text-foreground-400 line-through">¥{course.originalPrice}</span>
                      <span className="text-xs text-primary-600 font-medium ml-auto bg-primary-100 px-2 py-1 rounded-full">
                        限时优惠
                      </span>
                    </div>

                    <Link
                      to="/about"
                      className="block w-full text-center px-6 py-3.5 rounded-full bg-primary-500 text-background-50 font-semibold text-base hover:bg-primary-600 transition-colors mb-3 whitespace-nowrap"
                    >
                      立即报名
                    </Link>

                    <Link
                      to="/about"
                      className="block w-full text-center px-6 py-3.5 rounded-full border border-primary-300 text-primary-600 font-semibold text-base hover:bg-primary-50 transition-colors whitespace-nowrap"
                    >
                      免费试听
                    </Link>

                    <div className="mt-6 pt-6 border-t border-background-200 space-y-3">
                      <div className="flex items-center gap-2 text-sm text-foreground-600">
                        <i className="ri-check-line text-accent-600"></i>
                        <span>7天无理由退款</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-foreground-600">
                        <i className="ri-check-line text-accent-600"></i>
                        <span>课程终身回看</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-foreground-600">
                        <i className="ri-check-line text-accent-600"></i>
                        <span>专属社群答疑</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-foreground-600">
                        <i className="ri-check-line text-accent-600"></i>
                        <span>结业证书</span>
                      </div>
                    </div>

                    <div className="mt-6 pt-6 border-t border-background-200">
                      <h4 className="text-sm font-medium text-foreground-900 mb-2">相关课程</h4>
                      <div className="space-y-3">
                        {allCourses
                          .filter((c) => c.category === course.category && c.id !== course.id)
                          .slice(0, 3)
                          .map((rc) => (
                            <Link
                              key={rc.id}
                              to={`/article/${rc.slug}`}
                              className="flex items-center gap-3 group"
                            >
                              <img
                                src={rc.image}
                                alt={rc.title}
                                className="w-14 h-14 rounded-lg object-cover flex-shrink-0"
                              />
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors truncate">
                                  {rc.title}
                                </p>
                                <p className="text-xs text-foreground-500">¥{rc.price}</p>
                              </div>
                            </Link>
                          ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </>
    );
  }

  return null;
}

function activeCategoryName(catSlug: string): string {
  const map: Record<string, string> = {
    'it-programming': 'IT编程技术',
    'design-creative': '设计创意',
    'business-management': '商业管理',
    'language-learning': '语言学习',
    'vocational-skills': '职业技能',
    'certification': '资格证书',
  };
  return map[catSlug] || '相关';
}